import { Checkbox, TextArea } from '@progress/kendo-react-inputs';
import CustomButton from '../../../../../../components/stateless/customButton/CustomButton'
import ExpansionPanel from '../../../../../../components/stateless/expansionPanel/ExpansionPanel';
import { HearingNotice as HearingDef } from '../../../../../../utilities/interfaces/HearingNotice';
import { useHearingNoticeController } from './HearingNoticeController';
import CustomDialog from '../../../../../../components/stateless/dialog/CustomDialog';
import { Loader } from '@progress/kendo-react-indicators';


interface IHearingNoticeProps {
  newHearingNotice: boolean;
  saveHearingNotice: () => void;
  toggleHearingNoticeForm: () => void;
  filingFormDef: any;
  renderFormField: (item: any, index: number) => any;
  filingId: number;
  disabled: boolean;
}


/**
 *
 * @param newHearingNotice boolean - toggles the hearing notice view
 * @param saveHearingNotice function - () => void triggers create hearing notice function
 * @param toggleHearingNoticeForm function - () => void function toggles newHearingNotice and clears hearing notice values
 * @param filingFormDef object - from FilingFormDefinition specifing the filing form defintion
 * @param renderFormField function - (item:any{}, index:number) => React.Component
 * @returns React.Component
 */
const HearingNotice = (props: IHearingNoticeProps) => {
  const {
    newHearingNotice,
    saveHearingNotice,
    toggleHearingNoticeForm,
    filingFormDef,
    renderFormField,
    filingId,
    disabled
  } = props;
  const {
    //redux
    hearingNotices,
    filingActions,
    creatingHearingNotice,
    //data
    newCommentDialogOpen,
    newComment,
    confirmDeleteOpen,
    //functions
    toggleNewCommentDialog,
    saveNewComment,
    deleteHearingNotice,
    toggleConfirmDelete,
    parseUTCDate,
  } = useHearingNoticeController(filingId);

  const renderHearingNotices = () => {
    return (
      <>
        <CustomDialog
          open={confirmDeleteOpen}
          title='Confirm Delete'
          toggleDialog={toggleConfirmDelete}
          approveAction={() => {
            deleteHearingNotice();
            toggleConfirmDelete();
          }}
          cancelAction={toggleConfirmDelete}
          yesNo
          size='small'
        >
          Are you sure you want to delete this hearing notice?
        </CustomDialog>
        <CustomDialog
          open={newCommentDialogOpen}
          title='New Comment'
          toggleDialog={() => {toggleNewCommentDialog()}}
          approveAction={() => {
            saveNewComment();
            toggleNewCommentDialog();
          }}
          cancelAction={() => {toggleNewCommentDialog()}}
        >
          Name of Person Commenting
          <TextArea
            size="large"
            rows={1}
            title="Name of Person Commenting"
            onChange={(e) => {newComment.nameOfPersonCommenting = e.target.value ?? ""}}
            required
          />
          Affiliation of Person Commenting
          <TextArea
            size="large"
            rows={1}
            title="Affiliation of Person Commenting"
            onChange={(e) => {newComment.affiliationOfPersonCommenting = e.target.value ?? ""}}
            required
          />
          Comments
          <TextArea
            size="large"
            rows={4}
            title="Comments"
            onChange={(e) => {newComment.comment = e.target.value ?? ""}}
            required
          />
          Agency Response
          <TextArea
            size="large"
            rows={4}
            title="Agency Response"
            onChange={(e) => {newComment.agencyResponse = e.target.value ?? ""}}
            required
          />
        </CustomDialog>
        <div>
          {/* If hearing notice form is visible show save button */}
          {newHearingNotice
            ? (
              <>
                <CustomButton
                  onClick={saveHearingNotice}
                  icon='save'
                  disabled={disabled || creatingHearingNotice}
                >
                  {creatingHearingNotice && (<Loader type="converging-spinner" size='small' themeColor='light'/>)}
                  Save Hearing Notice
                </CustomButton>
                <CustomButton
                  onClick={toggleHearingNoticeForm}
                  icon="x"
                  themeColor="error"
                  disabled={disabled}
                >
                  Cancel
                </CustomButton>
              </>
            )
            : (
              <CustomButton
                title='New Hearing'
                onClick={toggleHearingNoticeForm}
                icon='add'
                themeColor='success'
                disabled={disabled}
              >
                New Hearing
              </CustomButton>
            )
          }
        </div>
        {
          newHearingNotice ? (
            filingFormDef.map(
              (item: any, index: number) => {
                return (renderFormField(item, index))
              }
            )
          ) : (
            // Loop over existing hearings and render
            hearingNotices.map((hearingNotice: HearingDef, index: number) => {
              return (
                <ExpansionPanel title={hearingNotice.hearingDescription ?? 'Hearing'} fullwidth item={{ id: 1 }}>
                  <div style={{marginBottom: "10px"}}>
                    <div><b>Description:</b> {hearingNotice.hearingDescription}</div>
                    <div><b>Filing Action:</b> {filingActions.find((action) => action.id === hearingNotice.filingActionId)?.filingActionName}</div>
                    <div><b>Hearing Date:</b> {parseUTCDate(hearingNotice.hearingDate)}</div>
                    <div><b>Address 1:</b> {hearingNotice.hearingAddressLine1}</div>
                    <div><b>Address 2:</b> {hearingNotice.hearingAddressLine2 ?? ''}</div>
                    <div><b>Hearing City:</b> {hearingNotice.hearingCity}</div>
                    <div><b>Hearing Postal Code:</b> {hearingNotice.hearingPostalCode}</div>
                    <div><b>State:</b> {hearingNotice.hearingState}</div>
                    <div><b>Agency Hearing Contact Name:</b> {hearingNotice.agencyHearingContactName ?? ''}</div>
                    <div><b>Agency Hearing Contact Email:</b> {hearingNotice.agencyHearingContactEmail ?? ''}</div>
                    <div><b>Agency Hearing Contact Phone:</b> {hearingNotice.agencyHearingContactPhone ?? ''}</div>
                    <div><b>Electronic Meeting Information:</b> {hearingNotice.hearingEMeetingInfo ?? ''}</div>
                  </div>

                  <div style={{fontSize: "1.75rem"}}>Comments</div>
                  <Checkbox defaultValue={hearingNotice.commentsAreLocked} onChange={(e) => {hearingNotice.commentsAreLocked = !!e.target.value}}> Lock comments for this hearing.</Checkbox>
                  <CustomButton
                    onClick={() => {toggleNewCommentDialog(hearingNotice.id)}}
                    themeColor='success'
                    icon='add'
                    disabled={disabled}
                  >
                    Add Comment
                  </CustomButton>
                  <CustomButton
                    onClick={() => {toggleConfirmDelete(hearingNotice.id)}}
                    themeColor='error'
                    icon='delete'
                    disabled={disabled}
                  >
                    Delete Notice
                  </CustomButton>
                  { hearingNotice.comments?.map((comment: any) => (
                    <ExpansionPanel title="Comment" fullwidth item={{id: 1}}>
                      Name of Person Commenting
                      <TextArea
                        size="large"
                        rows={1}
                        title="Name of Person Commenting"
                        defaultValue={comment.nameOfPersonCommenting}
                        onChange={() => {}}
                        readOnly={true}
                      />
                      Affiliation of Person Commenting
                      <TextArea
                        size="large"
                        rows={1}
                        title="Affiliation of Person Commenting"
                        defaultValue={comment.affiliationOfPersonCommenting}
                        onChange={() => {}}
                        readOnly={true}
                      />
                      Comments
                      <TextArea
                        size="large"
                        rows={4}
                        title="Comments"
                        defaultValue={comment.comment}
                        onChange={() => {}}
                        readOnly={true}
                      />
                      Agency Response
                      <TextArea
                        size="large"
                        rows={4}
                        title="Agency Response"
                        defaultValue={comment.agencyResponse}
                        onChange={() => {}}
                        readOnly={true}
                      />
                    </ExpansionPanel>
                  ))}
                </ExpansionPanel >
              );
            })
          )
        }

      </>
    );
  }

  return (
    <>
      {filingId && filingId !== -1 ? (
        renderHearingNotices()
      ) : (
        <h6>Please save/create the filing before creating hearing notices</h6>
      )}
    </>
  )
}

export default HearingNotice